// src/firebase.js
import { initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  deleteUser as authDeleteUser,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  addDoc,
  deleteDoc,
  doc,
  setDoc,
  updateDoc,
  writeBatch,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getStorage } from "firebase/storage";
import { getMessaging, getToken, onMessage } from "firebase/messaging";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const functions = getFunctions(app, "us-central1"); // Specify the region
const storage = getStorage(app);
// Initialize Firebase Cloud Messaging
const messaging = getMessaging(app);

console.log(
  "Firebase initialized. DB:",
  db,
  "Auth:",
  auth,
  "Functions:",
  functions,
);

const subscribeToComicsTopic = async (currentToken) => {
  try {
    const response = await fetch(
      `https://iid.googleapis.com/iid/v1/${currentToken}/rel/topics/comics`,
      {
        method: "POST",
        headers: {
          Authorization: `key=${process.env.REACT_APP_FCM_SERVER_KEY}`,
        },
      },
    );

    if (response.ok) {
      console.log("Successfully subscribed to comics topic");
    } else {
      console.error(
        "Error subscribing to comics topic:",
        response.status,
        response.statusText,
      );
    }
  } catch (error) {
    console.error("Error subscribing to comics topic:", error);
  }
};

export const requestForToken = () => {
  return getToken(messaging, {
    vapidKey:
      "BKG-ahVeeSCuHFzWsGZGlq83s_ehYNQR34CSmsErevTboCXLdTQ3o1MFdRYDgONbDNlpBrNio14UEYGu1XzSBQw", // Replace with your VAPID key
  })
    .then((currentToken) => {
      if (currentToken) {
        console.log("Current token for client: ", currentToken);
        // Track the token or send it to your server
        subscribeToComicsTopic(currentToken);
      } else {
        console.log(
          "No registration token available. Request permission to generate one.",
        );
      }
    })
    .catch((err) => {
      console.log("An error occurred while retrieving token. ", err);
    });
};

export const onMessageListener = () =>
  new Promise((resolve) => {
    onMessage(messaging, (payload) => {
      resolve(payload);
    });
  });

export const massUpdateCcpSchoolFlag = async () => {
  try {
    const batch = writeBatch(db);
    const q = query(
      collection(db, "users"),
      where("ccpDistrictFlag", "==", "yes"),
    );
    const querySnapshot = await getDocs(q);

    querySnapshot.forEach((doc) => {
      batch.update(doc.ref, { ccpDistrictFlag: true });
    });

    await batch.commit();

    console.log("Mass update complete!");
  } catch (error) {
    console.error("Error during mass update:", error);
    // Consider more robust error handling here (e.g., re-throw error)
  }
};

// Function to create a user without automatically logging them in
export const createUserCcpAdmin = async (
  email,
  password,
  name,
  phone,
  techPrepAdminFlag,
  ccpAdminFlag,
) => {
  try {
    const createUser = httpsCallable(functions, "createUserCcpAdmin");
    const result = await createUser({
      email,
      password,
      name,
      phone,
      techPrepAdminFlag,
      ccpAdminFlag,
    });
    return { success: true, message: "User created successfully" };
  } catch (error) {
    return { success: false, message: error.message };
  }
};
// Function to create a school user user without automatically logging them in
export const createSchoolUser = async (
  name,
  school_name,
  email,
  title,
  password,
  phone,
  ccpSchoolFlag,
  techPrepInstructorFlag,
  techPrepCoordFlag,
  userType,
  school_id,
) => {
  try {
    // Ensure user is authenticated so the Functions SDK will attach the ID token
    if (!auth.currentUser) {
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          unsubscribe && unsubscribe();
          reject(new Error("Auth state not established (timeout)"));
        }, 5000);
        let unsubscribe = auth.onAuthStateChanged((user) => {
          if (user) {
            clearTimeout(timeout);
            unsubscribe();
            resolve();
          }
        });
      });
    }

    const createUser = httpsCallable(functions, "createSchoolUser");
    const result = await createUser({
      name,
      school_name,
      email,
      title,
      password,
      phone,
      ccpSchoolFlag,
      techPrepInstructorFlag,
      techPrepCoordFlag,
      userType,
      school_id,
    });

    // Prefer server-returned structure (result.data)
    if (result && result.data) {
      return {
        success: !!result.data.success,
        message: result.data.message || "User created successfully",
      };
    }

    return { success: true, message: "User created successfully" };
  } catch (error) {
    console.error("Error creating school user:", error);

    // Map common function errors to friendly messages
    if (error && error.code === "functions/unauthenticated") {
      return {
        success: false,
        message: "You must be signed in to create users.",
        code: "unauthenticated",
      };
    }
    if (error && error.code === "functions/permission-denied") {
      return {
        success: false,
        message: "Insufficient permissions to create users.",
        code: "permission-denied",
      };
    }

    // Normal fallback
    return { success: false, message: error.message || String(error) };
  }
};
//function to create ccp district user
export const createCcpDistrictUser = async (
  name,
  district,
  email,
  password,
  phone,
  ccpDistrictFlag,
) => {
  try {
    const createUser = httpsCallable(functions, "createCcpDistrictUser");
    const result = await createUser({
      name,
      district,
      email,
      password,
      phone,
      ccpDistrictFlag,
    });
    return { success: true, message: "User created successfully" };
  } catch (error) {
    return { success: false, message: error.message };
  }
};
// Function to create a Tech Prep user without automatically logging them in
export const createTechPrepUser = async (
  email,
  password,
  firstName,
  lastName,
  phone,
  userTypes, // expects an object: { staff, coordinator, instructor }
  tp_partner,
) => {
  try {
    console.log("Firebase config: Calling createTechPrepUser with:", {
      email,
      firstName,
      lastName,
      phone,
      userTypes,
      tp_partner,
      passwordLength: password?.length,
    });

    const createUser = httpsCallable(functions, "createTechPrepUser");
    const result = await createUser({
      email,
      password,
      firstName,
      lastName,
      phone,
      userTypes,
      tp_partner,
    });

    console.log("Firebase config: User creation successful:", result);
    return { success: true, message: "User created successfully" };
  } catch (error) {
    console.error("Firebase config: Error creating user:", error);
    console.error("Firebase config: Error code:", error.code);
    console.error("Firebase config: Error message:", error.message);
    console.error("Firebase config: Error details:", error.details);

    // Return the specific error message from the cloud function
    return {
      success: false,
      message: error.message || "Error creating tech prep user",
    };
  }
};
// Creates a single Tech Prep Coordinator user (Auth account + users/{uid}
// doc) via the createTpCoordinatorUser callable, without signing the admin
// out of their own session. userData is merged directly into the Firestore
// doc (name, techPrepFlag, userTypes, and any other imported columns).
export const createTpCoordinatorUser = async (email, password, userData) => {
  try {
    // Ensure user is authenticated so the Functions SDK will attach the ID token
    if (!auth.currentUser) {
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          unsubscribe && unsubscribe();
          reject(new Error("Auth state not established (timeout)"));
        }, 5000);
        let unsubscribe = auth.onAuthStateChanged((user) => {
          if (user) {
            clearTimeout(timeout);
            unsubscribe();
            resolve();
          }
        });
      });
    }

    const createUser = httpsCallable(functions, "createTpCoordinatorUser");
    const result = await createUser({ email, password, userData });

    if (result && result.data) {
      return {
        success: !!result.data.success,
        uid: result.data.uid,
        message: result.data.message || "User created successfully",
      };
    }
    return { success: true, message: "User created successfully" };
  } catch (error) {
    console.error("Error creating Tech Prep coordinator user:", error);

    if (error && error.code === "functions/already-exists") {
      return {
        success: false,
        message: "A user with this email address already exists.",
        code: "already-exists",
      };
    }
    if (error && error.code === "functions/unauthenticated") {
      return {
        success: false,
        message: "You must be signed in to create users.",
        code: "unauthenticated",
      };
    }

    return {
      success: false,
      message: error.message || String(error),
      code: error.code,
    };
  }
};

// Creates a single Tech Prep Instructor user (Auth account + users/{uid}
// doc) via the createTpInstructorUser callable, without signing the admin
// out of their own session. userData is merged directly into the Firestore
// doc (name, techPrepFlag, userTypes, and any other imported columns).
export const createTpInstructorUser = async (email, password, userData) => {
  try {
    // Ensure user is authenticated so the Functions SDK will attach the ID token
    if (!auth.currentUser) {
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          unsubscribe && unsubscribe();
          reject(new Error("Auth state not established (timeout)"));
        }, 5000);
        let unsubscribe = auth.onAuthStateChanged((user) => {
          if (user) {
            clearTimeout(timeout);
            unsubscribe();
            resolve();
          }
        });
      });
    }

    const createUser = httpsCallable(functions, "createTpInstructorUser");
    const result = await createUser({ email, password, userData });

    if (result && result.data) {
      return {
        success: !!result.data.success,
        uid: result.data.uid,
        message: result.data.message || "User created successfully",
      };
    }
    return { success: true, message: "User created successfully" };
  } catch (error) {
    console.error("Error creating Tech Prep instructor user:", error);

    if (error && error.code === "functions/already-exists") {
      return {
        success: false,
        message: "A user with this email address already exists.",
        code: "already-exists",
      };
    }
    if (error && error.code === "functions/unauthenticated") {
      return {
        success: false,
        message: "You must be signed in to create users.",
        code: "unauthenticated",
      };
    }

    return {
      success: false,
      message: error.message || String(error),
      code: error.code,
    };
  }
};

// Function to update a user's password
export const updateUserPassword = async (uid, newPassword) => {
  try {
    const updatePassword = httpsCallable(functions, "updateUserPassword");
    const result = await updatePassword({
      uid,
      newPassword,
    });
    return { success: true, message: "Password updated successfully" };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

// Function to update a school user
export const updateSchoolUser = async (
  uid,
  name,
  school_name,
  email,
  title,
  phone,
  flag,
  userType,
  school_id,
) => {
  try {
    const updateUser = httpsCallable(functions, "updateSchoolUser");
    const result = await updateUser({
      uid,
      name,
      school_name,
      email,
      title,
      phone,
      flag,
      userType,
      school_id,
    });
    return { success: true, message: "User updated successfully" };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

// Function to update a TechPrep user (updates both Auth and Firestore)
export const updateTechPrepUser = async (userData) => {
  console.log("updateTechPrepUser called with data:", userData);

  try {
    // Ensure user is authenticated
    const user = auth.currentUser;
    if (!user) {
      console.error("No authenticated user found");
      return { success: false, message: "User not authenticated" };
    }

    console.log("Authenticated user:", user.uid);
    console.log("User auth token exists:", !!user.accessToken);
    console.log("Functions object:", functions);

    // Wait for auth state to be ready
    await new Promise((resolve) => {
      const unsubscribe = auth.onAuthStateChanged((authUser) => {
        if (authUser) {
          console.log("Auth state confirmed:", authUser.uid);
          unsubscribe();
          resolve();
        }
      });
    });

    console.log(
      "Calling cloud function with userData:",
      JSON.stringify(userData),
    );

    const updateUser = httpsCallable(functions, "updateTechPrepUser");
    console.log("httpsCallable function created:", updateUser);

    const result = await updateUser(userData);

    console.log("Cloud function result:", result);

    if (result.data && result.data.success) {
      return {
        success: true,
        message: result.data.message || "User updated successfully",
      };
    } else {
      return { success: false, message: "Unknown error occurred" };
    }
  } catch (error) {
    console.error("Error updating TechPrep user:", error);
    console.error("Error code:", error.code);
    console.error("Error message:", error.message);
    console.error("Error details:", error.details);
    console.error(
      "Full error object:",
      JSON.stringify(error, Object.getOwnPropertyNames(error)),
    );

    let errorMessage = "An error occurred while updating the user";

    if (error.code === "functions/unauthenticated") {
      errorMessage = "You must be logged in to update users";
    } else if (error.code === "functions/not-found") {
      errorMessage = "User not found";
    } else if (error.code === "functions/invalid-argument") {
      errorMessage = error.message || "Invalid data provided";
    } else if (error.message) {
      errorMessage = error.message;
    }

    return { success: false, message: errorMessage };
  }
};

// Function to delete a user
export const deleteUser = async (uid) => {
  try {
    // Ensure an authenticated user exists so Functions SDK will attach the ID token
    if (!auth.currentUser) {
      // Wait for auth state to be established (short timeout)
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          unsubscribe && unsubscribe();
          reject(new Error("Auth state not established (timeout)"));
        }, 5000);
        let unsubscribe = auth.onAuthStateChanged((user) => {
          if (user) {
            clearTimeout(timeout);
            unsubscribe();
            resolve();
          }
        });
      });
    }

    const deleteUserFn = httpsCallable(functions, "deleteUser");
    const result = await deleteUserFn({ uid });
    return result.data;
  } catch (error) {
    console.error("Error deleting user:", error);
    // If unauthenticated, try an HTTP fallback that we added server-side which accepts Bearer Token
    const isUnauthenticated =
      (error && error.code && error.code.includes("unauthenticated")) ||
      (error &&
        error.message &&
        error.message.toLowerCase().includes("unauthenticated"));

    if (isUnauthenticated && auth.currentUser) {
      try {
        const idToken = await auth.currentUser.getIdToken();
        const projectId =
          firebaseConfig.projectId || process.env.REACT_APP_PROJECT_ID;
        const url = `https://us-central1-${projectId}.cloudfunctions.net/deleteUserHttp`;
        const resp = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ uid }),
        });
        const data = await resp.json();
        return data;
      } catch (httpErr) {
        console.error("Fallback HTTP deleteUser failed:", httpErr);
        return { success: false, message: httpErr.message || String(httpErr) };
      }
    }

    // Normalize the error response to include useful fields for the UI
    if (error && error.code === "functions/unauthenticated") {
      return {
        success: false,
        message: "You must be signed in to delete users.",
        code: "unauthenticated",
      };
    }
    return { success: false, message: error.message || String(error) };
  }
};

// Frontend helper to call server-side updateUserEmail callable (admin-only)
export const updateUserEmail = async (uid, email) => {
  try {
    // Ensure auth state established so Functions SDK attaches ID token
    if (!auth.currentUser) {
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          unsubscribe && unsubscribe();
          reject(new Error("Auth state not established (timeout)"));
        }, 5000);
        let unsubscribe = auth.onAuthStateChanged((user) => {
          if (user) {
            clearTimeout(timeout);
            unsubscribe();
            resolve();
          }
        });
      });
    }

    const fn = httpsCallable(functions, "updateUserEmail");
    const result = await fn({ uid, email });
    if (result && result.data) {
      return { success: !!result.data.success, message: result.data.message };
    }
    return { success: true, message: "Email updated" };
  } catch (error) {
    console.error("updateUserEmail error:", error);
    if (error && error.code === "functions/permission-denied") {
      return {
        success: false,
        message: "Insufficient permissions to update email",
      };
    }
    return { success: false, message: error.message || String(error) };
  }
};

export { auth, db, app, authDeleteUser, storage };
